export interface Article {
  id: string;
  title: string;
  slug: string;
  category: string;
  categorySlug: string;
  summary: string;
  content: string;
  coverImage: string;
  date: string;
  author: string;
  tags: string[];
  featured: boolean;
}

export interface Category {
  name: string;
  slug: string;
  description: string;
  icon: string;
}

export const categories: Category[] = [
  {
    name: '剧情解析',
    slug: 'plot-analysis',
    description: '逐集深度解析艳姆1—6的剧情发展、关键转折与叙事结构',
    icon: 'ri-movie-2-line',
  },
  {
    name: '人物介绍',
    slug: 'character-intro',
    description: '全面了解艳姆1—6中各角色的性格特点、成长轨迹与人物关系',
    icon: 'ri-user-star-line',
  },
  {
    name: '系列亮点',
    slug: 'series-highlights',
    description: '探索艳姆1—6在艺术风格、配乐制作、声优阵容等方面的突出表现',
    icon: 'ri-sparkling-line',
  },
  {
    name: '观看指引',
    slug: 'viewing-guide',
    description: '提供艳姆1—6最佳观看顺序、资源获取及社区交流指引',
    icon: 'ri-guide-line',
  },
];

export const articles: Article[] = [
  {
    id: 'article-1',
    title: '艳姆1—6第1集剧情详解：命运的转折点',
    slug: 'episode-1-plot-analysis',
    category: '剧情解析',
    categorySlug: 'plot-analysis',
    summary: '深入解读艳姆系列开篇之作，从世界观的铺陈到核心矛盾的初次显现，全面剖析第1集中埋下的关键伏笔与人物命运的初次交汇。',
    content: `<h2>开篇：世界观的确立</h2>
<p>艳姆1—6系列的第1集以极具张力的开场镜头拉开了整个故事的序幕。制作团队通过精心设计的画面语言，在短短几分钟内便为观众构建了一个完整而富有层次感的世界观框架。从画面色调的冷暖对比，到背景音乐的情绪铺垫，每一个细节都在为后续剧情的发展埋下伏笔。</p>
<p>本集的核心在于"命运的转折"这一主题的初次呈现。主角团在看似平常的日常场景中，逐渐被卷入一场远超预期的冒险。这种从平凡到非凡的过渡处理得极为自然，让观众能够在不自觉中完成情感的代入。</p>

<h2>关键场景分析</h2>
<p>第1集中最为人称道的几个场景包括：开场的长镜头追逐戏、主角与关键配角的初次相遇、以及片尾令人震撼的剧情反转。其中开场追逐戏采用了极具动感的运镜方式，配合紧凑的剪辑节奏，营造出令人窒息的紧张氛围。</p>
<p>主角与配角的初次相遇场景则采用了完全不同的叙事手法——从紧张到舒缓的情绪转换，暗示着这段关系在后续剧情中的重要性。这种张弛有度的节奏把控，体现了制作团队对叙事节奏的精准掌控。</p>

<h2>伏笔与铺垫</h2>
<p>细心的观众可以发现，第1集中埋藏了大量后续剧情的线索。从主角随身携带的信物，到背景中不经意出现的符号标记，再到配角看似随意的台词，每一个细节都在为整个系列的宏大叙事服务。这种精密的伏笔设计，使得艳姆系列具备了极高的重刷价值。</p>

<h2>艺术表现</h2>
<p>在视觉呈现方面，第1集展现了极高的制作水准。角色设计细腻而富有个性，场景绘制精美且富有层次感。特别值得一提的是光影的运用——无论是室内场景的柔和光线，还是户外场景的强烈对比，都极大地增强了画面的戏剧张力。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20a%20dramatic%20scene%20with%20warm%20golden%20lighting%2C%20a%20silhouetted%20figure%20standing%20at%20a%20crossroads%20under%20a%20setting%20sun%2C%20atmospheric%20and%20emotional%2C%20soft%20cinematic%20composition%20with%20cherry%20blossom%20petals%20floating%2C%20warm%20amber%20and%20coral%20tones%2C%20anime%20film%20style%20illustration%20with%20beautiful%20gradients&width=800&height=450&seq=yanmu-ep1-cover&orientation=landscape',
    date: '2026-07-15',
    author: '艳姆解析团队',
    tags: ['第1集', '剧情解析', '世界观', '伏笔分析'],
    featured: true,
  },
  {
    id: 'article-2',
    title: '艳姆1—6第2集：情感的层层递进',
    slug: 'episode-2-plot-analysis',
    category: '剧情解析',
    categorySlug: 'plot-analysis',
    summary: '承接第1集的剧情发展，第2集将叙事重心转向角色内心世界的刻画，通过细腻的情感描写推进主线剧情。',
    content: `<h2>情感线索的展开</h2>
<p>如果说第1集是艳姆系列宏大叙事的序章，那么第2集则开始真正深入角色的内心世界。本集以更加内敛的叙事手法，将镜头对准了主角团在经历初次冒险后的心理变化。这种从外部冲突到内部情感的转换，为整个系列奠定了情感基调。</p>

<h2>角色关系的深化</h2>
<p>第2集最为精彩的段落莫过于主角与几位关键配角之间的互动戏份。通过日常对话、共同面对困难等场景，角色之间的羁绊得到了充分的展现和深化。特别是那段雨中的对话场景——雨声、配乐与台词的三重交织，达到了极高的艺术感染力。</p>

<h2>剧情推进</h2>
<p>在情感描写的同时，第2集也没有忽视主线的推进。通过巧妙的信息释放节奏，观众在感受角色情感变化的同时，也在逐步接近故事的核心谜团。这种双线并行的叙事结构，是本集最大的亮点之一。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Anime%20illustration%20of%20two%20figures%20sharing%20an%20emotional%20moment%20under%20rain%2C%20warm%20street%20lamp%20glow%20reflecting%20on%20wet%20pavement%2C%20intimate%20atmospheric%20scene%2C%20soft%20watercolor%20inspired%20anime%20style%2C%20warm%20coral%20and%20amber%20color%20palette%2C%20gentle%20emotional%20composition&width=800&height=450&seq=yanmu-ep2-cover&orientation=landscape',
    date: '2026-07-18',
    author: '艳姆解析团队',
    tags: ['第2集', '剧情解析', '情感描写', '角色关系'],
    featured: true,
  },
  {
    id: 'article-3',
    title: '艳姆1—6第3集：冲突与和解的交织',
    slug: 'episode-3-plot-analysis',
    category: '剧情解析',
    categorySlug: 'plot-analysis',
    summary: '第3集迎来了系列的第一个剧情高潮，内部矛盾与外部威胁同时爆发，角色们面临前所未有的考验。',
    content: `<h2>矛盾升级</h2>
<p>第3集标志着艳姆系列进入了第一个剧情高潮阶段。此前埋下的多条伏笔在本集中开始集中爆发——团队内部的信任危机、外部敌对势力的步步紧逼、以及主角个人信念的动摇，三者交织在一起，构成了极具张力的叙事网络。</p>

<h2>高潮场景回顾</h2>
<p>本集的高潮打斗场景堪称系列迄今为止的视觉巅峰。制作团队在此段落中投入了大量资源，无论是动作设计的流畅度、特效渲染的精细度，还是配乐与画面的同步性，都达到了极高的水准。这段场景不仅是视觉的盛宴，更是角色情感爆发的集中体现。</p>

<h2>和解与成长</h2>
<p>在经历了激烈的冲突之后，第3集的结尾部分转向了和解与成长的主题。角色们在危机中学会了信任与协作，团队关系在经历考验后变得更加牢固。这种"先破后立"的叙事策略，让角色的成长弧线显得格外真实而动人。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Dramatic%20anime%20action%20scene%20with%20dynamic%20composition%2C%20two%20warriors%20clashing%20in%20midair%20against%20a%20glowing%20sunset%20sky%2C%20energy%20effects%20swirling%2C%20warm%20orange%20and%20gold%20tones%2C%20cinematic%20anime%20style%20with%20dramatic%20lighting%20and%20motion%20blur%20effects&width=800&height=450&seq=yanmu-ep3-cover&orientation=landscape',
    date: '2026-07-22',
    author: '艳姆解析团队',
    tags: ['第3集', '剧情解析', '战斗场景', '角色成长'],
    featured: false,
  },
  {
    id: 'article-4',
    title: '艳姆1—6第4集：内心世界的深度剖析',
    slug: 'episode-4-plot-analysis',
    category: '剧情解析',
    categorySlug: 'plot-analysis',
    summary: '在外部冲突暂告段落后，第4集将视角深入角色的内心世界，通过回忆与梦境交织的手法揭示关键背景故事。',
    content: `<h2>叙事手法的创新</h2>
<p>第4集是艳姆系列在叙事手法上的一次大胆尝试。本集大量运用了非线性叙事——通过回忆、梦境、幻觉等手法，将角色的过去与现在交织呈现。这种叙事方式不仅丰富了故事的表现层次，更让观众对角色有了更加立体的理解。</p>

<h2>背景故事揭秘</h2>
<p>通过主角的回忆段落，第4集揭开了系列中一些关键谜团的答案。从主角童年时期的经历，到导致当前局面的关键事件，这些背景信息的释放节奏经过精心设计——既不会一次性倾泻太多信息导致观众难以消化，也不会过于吝啬而让人感到困惑。</p>

<h2>心理学视角的解读</h2>
<p>从心理学角度来看，第4集对角色内心创伤的描绘极具深度。制作团队显然在角色心理塑造上投入了大量研究，无论是创伤后应激反应的呈现，还是角色应对机制的表现，都具有相当的专业性和真实感。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Surreal%20anime%20art%20of%20a%20figure%20surrounded%20by%20floating%20memory%20fragments%20and%20mirrors%2C%20dreamlike%20ethereal%20atmosphere%2C%20soft%20pastel%20and%20warm%20tone%20color%20palette%2C%20abstract%20composition%20with%20fragmented%20imagery%2C%20psychological%20art%20style%2C%20beautiful%20watercolor%20texture&width=800&height=450&seq=yanmu-ep4-cover&orientation=landscape',
    date: '2026-07-25',
    author: '艳姆解析团队',
    tags: ['第4集', '剧情解析', '内心世界', '背景故事'],
    featured: false,
  },
  {
    id: 'article-5',
    title: '艳姆1—6第5集：高潮迭起的剧情转折',
    slug: 'episode-5-plot-analysis',
    category: '剧情解析',
    categorySlug: 'plot-analysis',
    summary: '作为系列倒数第二集，第5集以惊人的节奏推进剧情，多个关键转折点接连爆发，为最终集做好了完美铺垫。',
    content: `<h2>节奏的加速</h2>
<p>进入第5集，艳姆系列的叙事节奏明显加快。如果说前4集是在精心铺设一张巨大的叙事网络，那么第5集就是开始收网的时刻。本集的信息密度远超此前任何一集，但制作团队通过精妙的节奏控制，确保了观众能够跟上剧情的快速推进。</p>

<h2>关键转折</h2>
<p>第5集中至少包含了三个足以改变整个故事走向的关键转折点。每一个转折都建立在之前剧情的充分铺垫之上，因此即便转折来得突然，也不会让观众感到突兀。这种"意料之外、情理之中"的叙事效果，正是优秀剧本创作的标志。</p>

<h2>为终章铺路</h2>
<p>作为倒数第二集，第5集肩负着为最终集做好一切准备的重任。从各方势力的最终集结、到关键道具的亮相、再到角色关系的最终定位，本集将所有的叙事线索都推向了即将爆发的临界点。这种完美的铺垫，让观众对大结局充满了期待。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Epic%20anime%20scene%20showing%20multiple%20characters%20converging%20at%20a%20dramatic%20climax%2C%20stormy%20sky%20with%20lightning%2C%20characters%20in%20dynamic%20poses%2C%20warm%20golden%20light%20breaking%20through%20dark%20clouds%2C%20cinematic%20widescreen%20composition%2C%20intense%20emotional%20atmosphere&width=800&height=450&seq=yanmu-ep5-cover&orientation=landscape',
    date: '2026-07-28',
    author: '艳姆解析团队',
    tags: ['第5集', '剧情解析', '转折点', '终章铺垫'],
    featured: true,
  },
  {
    id: 'article-6',
    title: '艳姆1—6第6集：完美收官与深远寓意',
    slug: 'episode-6-plot-analysis',
    category: '剧情解析',
    categorySlug: 'plot-analysis',
    summary: '系列最终集以令人满意的结局为整个故事画上句号，同时通过深刻的主题表达为观众留下了长久的思考空间。',
    content: `<h2>终章的艺术</h2>
<p>作为艳姆1—6系列的收官之作，第6集承担着为整个系列画上圆满句号的重任。从最终的战斗场面到角色命运的尘埃落定，每一个场景都经过了精心的打磨。本集长达40分钟的篇幅，为制作团队提供了充足的空间来完成这场史诗般的终章。</p>

<h2>结局解读</h2>
<p>第6集的结局设计堪称精妙——它既给了主要角色们一个明确的归宿，又在某些细节上保留了开放式解读的空间。这种"实中有虚"的处理方式，既满足了观众对圆满结局的期待，又为系列的讨论和解读留下了持续的可能性。</p>

<h2>主题升华</h2>
<p>在解决所有剧情冲突之后，第6集的最后段落将叙事提升到了哲学层面。通过主角的一段独白和精心设计的蒙太奇画面，系列的核心主题——关于选择、牺牲与成长——得到了最终的升华。这段结尾的处理，使得艳姆系列超越了普通娱乐作品的范畴，具备了更深层的人文思考价值。</p>

<h2>系列遗产</h2>
<p>回顾整个艳姆1—6系列，可以清晰地看到一部优秀作品应有的所有品质：精良的制作水准、扎实的剧本基础、丰富的人物塑造、以及深刻的主题表达。第6集作为这个系列的句号，不仅出色地完成了自身的叙事任务，更为整个系列赋予了完整的意义。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Beautiful%20anime%20ending%20scene%20with%20sunrise%20over%20a%20peaceful%20landscape%2C%20characters%20silhouetted%20against%20the%20golden%20morning%20light%2C%20cherry%20blossoms%20drifting%20in%20the%20air%2C%20hopeful%20and%20serene%20atmosphere%2C%20warm%20and%20soft%20color%20palette%2C%20emotional%20closure%20and%20new%20beginning%20theme&width=800&height=450&seq=yanmu-ep6-cover&orientation=landscape',
    date: '2026-08-01',
    author: '艳姆解析团队',
    tags: ['第6集', '剧情解析', '结局', '主题升华'],
    featured: true,
  },
  {
    id: 'article-7',
    title: '艳姆1—6主要人物全解析：从主角到配角的性格画像',
    slug: 'character-full-analysis',
    category: '人物介绍',
    categorySlug: 'character-intro',
    summary: '逐一剖析艳姆1—6系列中每一位重要角色的性格特质、成长弧线、人际关系及其在整体叙事中的功能定位。',
    content: `<h2>主角团深度剖析</h2>
<p>艳姆1—6系列的角色塑造是其最受赞誉的方面之一。每一位主要角色都具有鲜明的个性特征和完整的成长曲线。主角作为故事的核心驱动力，其性格的多面性在6集的篇幅中得到了充分展现——从最初的犹豫不决到最终的坚定果敢，这种转变的每一个阶段都有扎实的剧情支撑。</p>

<h2>配角的功能与魅力</h2>
<p>值得特别指出的是，艳姆系列中的配角并非简单的功能性存在。每一位配角都拥有独立的人格和动机，他们在推动主线剧情的同时，也在完成属于自己的故事。例如，亦敌亦友的关键配角，其复杂的立场转换和内心的道德挣扎，构成了系列中最引人深思的支线之一。</p>

<h2>人物关系图谱</h2>
<p>艳姆系列中的人物关系设计颇具匠心。通过信任、背叛、救赎等多重关系维度，角色之间形成了一个紧密而富有张力的关系网络。这种复杂而真实的人际关系描绘，是艳姆系列区别于同类作品的重要特征。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Anime%20character%20design%20showcase%20with%20multiple%20diverse%20characters%20arranged%20in%20a%20dynamic%20group%20composition%2C%20each%20with%20distinct%20personality%20and%20design%2C%20warm%20lighting%20from%20above%2C%20professional%20anime%20art%20style%2C%20rich%20detail%20and%20expressive%20poses&width=800&height=450&seq=yanmu-char-cover&orientation=landscape',
    date: '2026-08-03',
    author: '艳姆解析团队',
    tags: ['人物介绍', '角色分析', '性格画像', '关系图谱'],
    featured: true,
  },
  {
    id: 'article-8',
    title: '艳姆1—6系列艺术风格与制作亮点',
    slug: 'art-style-and-production',
    category: '系列亮点',
    categorySlug: 'series-highlights',
    summary: '从视觉设计、色彩运用、场景绘制到动作设计，全面解读艳姆1—6系列在艺术表现层面的卓越成就。',
    content: `<h2>视觉风格的独特性</h2>
<p>艳姆1—6系列在视觉风格上形成了一套极具辨识度的美学体系。从角色设计的线条风格到场景绘制的色彩搭配，都展现出了制作团队深厚的艺术功底。特别是该系列对"光影叙事"的运用——通过光线的强弱、角度和色温的变化来传达情绪和暗示剧情走向——达到了艺术表现的新高度。</p>

<h2>色彩叙事</h2>
<p>色彩在艳姆系列中不仅是视觉装饰，更是一种叙事工具。第1集中冷色调与暖色调的对比暗示了角色立场的对立，第3集中逐渐升温的色调变化呼应了角色关系的和解，第6集中金色调的广泛使用则象征着希望与新生。这种精细的色彩叙事设计，让每一帧画面都具有解读的价值。</p>

<h2>动作场景设计</h2>
<p>艳姆系列的动作场景设计融合了东方武术的韵律美与现代动画的动态表现力。每一场战斗不仅是力量的碰撞，更是角色性格和情感的外化表达。制作团队在动作设计中融入了大量的细节——从角色的微表情到服饰的飘动——这些细节的累积造就了极具沉浸感的观看体验。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Beautiful%20anime%20art%20style%20showcase%2C%20detailed%20character%20design%20with%20flowing%20fabric%20and%20dynamic%20lighting%2C%20rich%20color%20gradients%20and%20atmospheric%20effects%2C%20professional%20animation%20production%20quality%2C%20warm%20and%20vibrant%20color%20palette%2C%20artistic%20composition&width=800&height=450&seq=yanmu-art-cover&orientation=landscape',
    date: '2026-08-05',
    author: '艳姆解析团队',
    tags: ['系列亮点', '艺术风格', '制作水准', '视觉设计'],
    featured: false,
  },
  {
    id: 'article-9',
    title: '艳姆1—6观看顺序与最佳体验指南',
    slug: 'viewing-order-guide',
    category: '观看指引',
    categorySlug: 'viewing-guide',
    summary: '为不同需求的观众提供多种观看方案：初次观看的推荐顺序、深度研究的观看路径、以及配套资源的获取建议。',
    content: `<h2>推荐观看顺序</h2>
<p>对于初次接触艳姆系列的观众，我们强烈建议按照1—6集的自然顺序进行观看。该系列在剧情设计上采用了严格的线性递进结构，每一集都建立在之前剧情的基础之上。跳过任何一集都可能导致对后续剧情理解的偏差。</p>

<h2>深度观看建议</h2>
<p>对于希望深入理解艳姆系列的观众，建议采用"三遍观看法"：第一遍关注主线剧情，第二遍留意细节伏笔，第三遍聚焦视听语言。这种递进式的观看方法能够帮助观众逐步挖掘作品的多层内涵。</p>

<h2>最佳观看环境</h2>
<p>为了获得最佳的观看体验，建议在安静的环境中，使用高质量耳机或音响设备观看。艳姆系列的配乐和声效设计极为精细，普通的播放设备可能无法充分展现其音效设计的层次感。此外，建议选择画质较高的播放源，以充分体验作品的视觉美感。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Cozy%20home%20viewing%20setup%20with%20a%20large%20screen%20displaying%20colorful%20anime%20art%2C%20dim%20warm%20ambient%20lighting%2C%20comfortable%20environment%2C%20immersive%20entertainment%20atmosphere%2C%20warm%20and%20inviting%20interior%2C%20cinematic%20photography%20style&width=800&height=450&seq=yanmu-guide-cover&orientation=landscape',
    date: '2026-08-07',
    author: '艳姆解析团队',
    tags: ['观看指引', '观看顺序', '体验指南', '资源推荐'],
    featured: false,
  },
  {
    id: 'article-10',
    title: '艳姆1—6幕后团队与创作故事',
    slug: 'behind-the-scenes',
    category: '系列亮点',
    categorySlug: 'series-highlights',
    summary: '揭秘艳姆1—6系列背后的核心创作团队，从导演、编剧到原画师的创作理念与幕后故事。',
    content: `<h2>核心创作团队</h2>
<p>艳姆1—6系列汇集了一批业内顶尖的创作人才。导演以其独特的叙事美学和对情感细节的精准把控而闻名，编剧团队则在故事结构和角色塑造上展现了深厚的功力。每一位核心成员都为这个系列注入了独特的创作基因。</p>

<h2>创作理念</h2>
<p>根据制作团队的访谈资料，艳姆系列的创作初衷是打造一部"既有娱乐性，又不失深度"的作品。创作团队在剧本阶段就确立了"情感驱动叙事"的核心理念，即所有剧情的发展都应该服务于角色的情感成长。这一理念贯穿了整个系列的创作过程。</p>

<h2>幕后趣闻</h2>
<p>在制作过程中，发生了许多有趣的幕后故事。例如，第3集中的经典打斗场景实际上经过了17次修改才最终定稿；第4集梦境段落的美术设计灵感来源于团队中一位原画师的个人经历；而第6集结尾的经典台词，是编剧在截稿前的深夜灵感迸发的产物。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Anime%20production%20studio%20workspace%20with%20artists%20working%20at%20desks%2C%20storyboards%20and%20character%20sketches%20on%20walls%2C%20warm%20creative%20atmosphere%2C%20professional%20animation%20studio%20environment%2C%20soft%20lighting%20and%20organized%20creative%20chaos&width=800&height=450&seq=yanmu-bts-cover&orientation=landscape',
    date: '2026-08-09',
    author: '艳姆解析团队',
    tags: ['系列亮点', '幕后团队', '创作故事', '制作花絮'],
    featured: false,
  },
  {
    id: 'article-11',
    title: '艳姆1—6配乐与声优阵容深度解读',
    slug: 'music-and-voice-cast',
    category: '系列亮点',
    categorySlug: 'series-highlights',
    summary: '深度分析艳姆1—6系列中令人印象深刻的配乐创作，以及豪华声优阵容为角色注入的灵魂演绎。',
    content: `<h2>配乐的艺术</h2>
<p>艳姆1—6系列的配乐由知名作曲家操刀，整张原声带融合了管弦乐的宏大与电子乐的现代感，创造出独具特色的听觉体验。主题曲以其动人的旋律和贴合剧情的歌词，在上线后迅速获得了广泛好评。插曲的运用同样精准——每一段配乐的出现时机、音量和情绪都经过了精心的设计。</p>

<h2>声优阵容</h2>
<p>艳姆系列集结了业内顶尖的声优阵容。主角的声优以其高辨识度的声线和精准的情感表达，成功塑造了一个令人难忘的角色形象。几位主要配角的声优同样表现出色，他们通过细微的语气变化和情感投入，为各自的角色赋予了鲜活的生命力。</p>

<h2>声音设计</h2>
<p>除了配乐和配音，艳姆系列在环境音效和特殊音效的设计上也展现了极高的水准。从风吹树叶的细微声响，到战斗场景中的能量爆发音效，每一个声音细节都经过了精心的打磨。这种对声音品质的极致追求，是艳姆系列整体制作水准的重要体现。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20music%20recording%20studio%20with%20orchestra%20instruments%20and%20sheet%20music%2C%20warm%20amber%20lighting%2C%20cinematic%20atmosphere%2C%20musical%20notes%20visualized%20as%20floating%20light%20particles%2C%20professional%20sound%20production%20environment&width=800&height=450&seq=yanmu-music-cover&orientation=landscape',
    date: '2026-08-10',
    author: '艳姆解析团队',
    tags: ['系列亮点', '配乐分析', '声优阵容', '声音设计'],
    featured: false,
  },
  {
    id: 'article-12',
    title: '艳姆1—6粉丝社区与作品影响力分析',
    slug: 'fan-community-impact',
    category: '观看指引',
    categorySlug: 'viewing-guide',
    summary: '探讨艳姆1—6系列在观众群体中引发的热烈讨论、二次创作现象及其在动画文化领域的深远影响。',
    content: `<h2>粉丝社区生态</h2>
<p>自艳姆1—6系列上线以来，围绕该作品形成了活跃而富有创造力的粉丝社区。从剧情讨论到角色分析，从同人创作到周边收藏，粉丝们的热情参与极大地丰富了作品的文化生态。各大社交平台上的相关话题讨论量持续攀升，反映出作品在观众群体中的广泛影响力。</p>

<h2>二次创作现象</h2>
<p>艳姆系列引发了大量的二次创作热潮。粉丝们以绘画、小说、视频剪辑等多种形式表达对作品的喜爱和理解。其中不乏质量极高的作品，有些二次创作甚至获得了原作团队的关注和认可。这种现象不仅证明了作品的感染力，也体现了艳姆系列作为文化IP的延展潜力。</p>

<h2>文化影响力</h2>
<p>从更宏观的角度来看，艳姆1—6系列在一定程度上推动了动画创作领域对情感深度的重视。该系列的成功证明了观众对"有深度"的动画作品的强烈需求，为行业树立了新的创作标杆。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Vibrant%20anime%20fan%20community%20gathering%20with%20colorful%20fan%20art%20displayed%20on%20walls%2C%20people%20discussing%20and%20sharing%2C%20warm%20and%20energetic%20atmosphere%2C%20creative%20convention%20style%20environment%2C%20diverse%20and%20inclusive%20community%20scene&width=800&height=450&seq=yanmu-fan-cover&orientation=landscape',
    date: '2026-08-11',
    author: '艳姆解析团队',
    tags: ['观看指引', '粉丝社区', '影响力', '二次创作'],
    featured: false,
  },
];

export const getArticlesByCategory = (categorySlug: string): Article[] => {
  return articles.filter((a) => a.categorySlug === categorySlug);
};

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((a) => a.slug === slug);
};

export const getFeaturedArticles = (): Article[] => {
  return articles.filter((a) => a.featured);
};

export const getRelatedArticles = (currentSlug: string, limit: number = 3): Article[] => {
  const current = getArticleBySlug(currentSlug);
  if (!current) return [];
  return articles
    .filter((a) => a.slug !== currentSlug && a.categorySlug === current.categorySlug)
    .slice(0, limit);
};